import { makeStyles } from '@material-ui/core/styles'
import { Theme, Typography, Grid, Box } from '@material-ui/core'
import { useFormikContext } from 'formik'
import { FormattedMessage } from 'react-intl'
import {
  SemiConfigFormValues,
  specificationItem,
} from '../ProductsConfiguration'
import SpecificationRadio from './SpecificationRadio'
import HoverToolTip from '@src/components/HoverToolTip'

const useStyles = makeStyles((theme: Theme) => ({
  specificationItem: {
    paddingTop: theme.spacing(4),
  },
  recommendation: {
    padding: theme.spacing(1, 0, 2),
  },
  popperClass: {
    maxWidth: '520px',
  },
  tooltipClass: {
    maxWidth: 'none',
    width: 'auto',
    padding: theme.spacing(4),
  },
  specificationItemWithInfo: {
    paddingTop: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
  },
  specificationInfoMessage: {
    paddingTop: theme.spacing(1),
  },
}))
interface RNASpecificationsProps {
  speciesList: specificationItem[]
  structureList: specificationItem[]
  scaleList: specificationItem[]
  modificationList: specificationItem[]
  purificationList: specificationItem[]
  formatList: specificationItem[]
  onChange: (f: string, v: string) => void
}

const RNASpecifications = ({
  speciesList,
  structureList,
  scaleList,
  modificationList,
  purificationList,
  formatList,
  onChange,
}: RNASpecificationsProps) => {
  const classes = useStyles()
  const formik = useFormikContext<SemiConfigFormValues>()

  return (
    <Grid container>
      <Grid item xs={12} className={classes.specificationItem}>
        <Typography variant="h3">
          <FormattedMessage
            id="CONF_SPECIFICATION_CRISPR_SPECIES_crisprgrna"
            defaultMessage="CRISPR Species"
          />
        </Typography>
      </Grid>
      <SpecificationRadio
        list={speciesList}
        specificationName="species"
        specificationValue={formik.values.specifications.species}
        handleChange={onChange}
        showRequiredError
      />
      <Grid item xs={12} className={classes.specificationItem}>
        <Typography variant="h3">
          *<FormattedMessage id="STRUCTURE" defaultMessage="Structure" />
        </Typography>
      </Grid>
      <SpecificationRadio
        list={structureList}
        specificationName="structure"
        specificationValue={formik.values.specifications.structure}
        handleChange={onChange}
        showRequiredError
      />
      <Grid item xs={12} className={classes.specificationItem}>
        <Typography variant="h3">
          *
          <FormattedMessage
            id="CONF_SPECIFICATION_SYNTHESIS_SCALE_crisprgrna"
            defaultMessage="Synthesis Scale"
          />
        </Typography>
      </Grid>
      <SpecificationRadio
        list={scaleList}
        specificationName="scale"
        specificationValue={formik.values.specifications.scale}
        handleChange={onChange}
        showRequiredError
      />
      <Grid item xs={12} className={classes.specificationItem}>
        <Typography variant="h3">
          *
          <FormattedMessage
            id="CONF_SPECIFICATION_MODIFIED_crisprgrna"
            defaultMessage="Modified"
          />
        </Typography>
        <Typography
          variant="body2"
          className={classes.specificationInfoMessage}
        >
          <FormattedMessage
            id="SEMICONF_CRISPRGRNA_MODIFICATION_HELP_MESSAGE"
            defaultMessage="Chemically modified synthetic gRNA contains stabilizing 2’-O-methyl and phosphorothioate linkages, improving editing efficiency."
          />
        </Typography>
      </Grid>
      <SpecificationRadio
        list={modificationList}
        specificationName="modification"
        specificationValue={formik.values.specifications.modification}
        handleChange={onChange}
        showRequiredError
      />
      <Grid item xs={12} className={classes.specificationItemWithInfo}>
        <Typography variant="h3">
          *
          <FormattedMessage
            id="CONF_PURIFICATION"
            defaultMessage="Purification"
          />
        </Typography>
        <HoverToolTip
          fontSize="19.25"
          popperClass={classes.popperClass}
          tooltipClass={classes.tooltipClass}
          toolTipText={
            <Box>
              <Typography variant="body2">
                <FormattedMessage
                  id="SEMICONF_crisprgrna_purification_help"
                  defaultMessage="Standard sgRNAs are purified using reverse-phase cartridge chromatography, 
                    effectively separating full-length sequences from shorter or truncated ones, making them suitable for 
                    various CRISPR applications across model systems."
                />
              </Typography>
              <br />
              <Typography variant="body2">
                <FormattedMessage
                  id="SEMICONF_crisprgrna_purification_help_two"
                  defaultMessage="In contrast, PURedit™ sgRNAs are purified via HPLC, ensuring the highest quality by precisely 
                    removing truncated sequences with minimal base changes. For applications that demand top-tier reagents, PURedit™ 
                    sgRNAs are the optimal choice."
                />
              </Typography>
            </Box>
          }
        />
      </Grid>
      <SpecificationRadio
        list={purificationList}
        specificationName="purification"
        specificationValue={formik.values.specifications.purification}
        handleChange={onChange}
        showRequiredError
      />
      <Grid item xs={12} className={classes.specificationItem}>
        <Typography variant="h3">
          <FormattedMessage
            id="CONF_SPECIFICATION_FORMAT_crisprgrna"
            defaultMessage="Format"
          />
        </Typography>
      </Grid>
      <SpecificationRadio
        list={formatList}
        specificationName="format"
        specificationValue={formik.values.specifications.format}
        handleChange={onChange}
      />
    </Grid>
  )
}

export default RNASpecifications
